/**
 * Spatial Analysis Engine
 *
 * Core service for performing spatial analysis operations using Turf.js.
 * Supports buffer, intersection, union, clip, and spatial query operations.
 *
 * **Validates: Requirements 22.1, 22.2, 22.3, 22.5, 22.6, 23.1, 23.2, 23.3, 23.4, 23.5,
 *              24.1, 24.2, 24.4, 24.5, 25.1, 25.2, 25.4, 25.5, 26.1, 26.2, 26.3, 26.6**
 */

import buffer from '@turf/buffer';
import intersect from '@turf/intersect';
import union from '@turf/union';
import booleanContains from '@turf/boolean-contains';
import booleanWithin from '@turf/boolean-within';
import booleanIntersects from '@turf/boolean-intersects';
import booleanTouches from '@turf/boolean-touches';
import booleanCrosses from '@turf/boolean-crosses';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import lineIntersect from '@turf/line-intersect';
import polygonToLine from '@turf/polygon-to-line';
import { featureCollection, lineString, point } from '@turf/helpers';
import type {
  Feature,
  FeatureCollection,
  Geometry,
  Polygon,
  MultiPolygon,
  LineString,
  MultiLineString,
  Point,
  Position,
  GeoJsonProperties,
} from 'geojson';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DistanceUnits = 'meters' | 'kilometers' | 'feet' | 'miles';
export type SpatialRelationship =
  | 'contains'
  | 'within'
  | 'intersects'
  | 'touches'
  | 'crosses'
  | 'within-distance';

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface VectorLayerData {
  id: string;
  name: string;
  geometryType?: 'Point' | 'LineString' | 'Polygon' | 'MultiPolygon';
  features: Feature[];
}

export interface BufferOptions {
  distance: number;
  units: DistanceUnits;
  rings?: number; // Number of concentric buffer rings
}

export interface SpatialQueryOptions {
  referenceFeature: Feature;
  targetLayer: VectorLayerData;
  relationship: SpatialRelationship;
  distance?: number;
  distanceUnits?: DistanceUnits;
}

export interface SpatialAnalysisResult {
  success: boolean;
  features: Feature[];
  message?: string;
  layerName?: string;
}

// ---------------------------------------------------------------------------
// Unit conversion helpers
// ---------------------------------------------------------------------------

/** Convert DistanceUnits to Turf.js units string */
function toTurfUnits(units: DistanceUnits): 'meters' | 'kilometers' | 'feet' | 'miles' {
  return units;
}

// ---------------------------------------------------------------------------
// Spatial Analysis Engine
// ---------------------------------------------------------------------------

export class SpatialAnalysisEngine {
  /**
   * Creates buffer polygons around features at specified distance(s).
   * Supports multiple concentric buffer rings.
   *
   * Requirements: 22.2, 22.3, 22.5, 22.6
   */
  buffer(features: Feature[], options: BufferOptions): SpatialAnalysisResult {
    const { distance, units, rings = 1 } = options;

    if (!features || features.length === 0) {
      return { success: false, features: [], message: 'No features provided for buffer operation' };
    }

    if (distance <= 0) {
      return { success: false, features: [], message: 'Buffer distance must be positive' };
    }

    if (rings < 1) {
      return { success: false, features: [], message: 'Number of rings must be at least 1' };
    }

    const turfUnits = toTurfUnits(units);
    const resultFeatures: Feature[] = [];

    try {
      for (const feat of features) {
        for (let ring = 1; ring <= rings; ring++) {
          const ringDistance = (distance / rings) * ring;
          const buffered = buffer(feat, ringDistance, { units: turfUnits });

          if (buffered) {
            // Add metadata properties
            const props: GeoJsonProperties = {
              ...feat.properties,
              _bufferDistance: ringDistance,
              _bufferUnits: units,
              _bufferRing: ring,
              _sourceFeatureId: feat.id || feat.properties?.id,
            };
            buffered.properties = props;
            resultFeatures.push(buffered as Feature);
          }
        }
      }

      // Validate results
      const invalidFeatures = resultFeatures.filter(
        (f) => !this.validateGeometry(f.geometry).valid
      );

      if (invalidFeatures.length > 0) {
        return {
          success: true,
          features: resultFeatures,
          message: `Buffer created with ${invalidFeatures.length} topologically invalid result(s)`,
        };
      }

      return {
        success: true,
        features: resultFeatures,
        message: `Created ${resultFeatures.length} buffer feature(s)`,
      };
    } catch (error) {
      return {
        success: false,
        features: [],
        message: `Buffer operation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Computes geometric intersection of two layers.
   * Preserves attributes from both input layers.
   *
   * Requirements: 23.1, 23.2, 23.3, 23.4, 23.5
   */
  intersection(layer1: VectorLayerData, layer2: VectorLayerData): SpatialAnalysisResult {
    if (!layer1.features.length || !layer2.features.length) {
      return {
        success: true,
        features: [],
        message: 'No intersections found - one or both layers are empty',
      };
    }

    const resultFeatures: Feature[] = [];

    try {
      for (const feat1 of layer1.features) {
        for (const feat2 of layer2.features) {
          // intersect only works with polygons
          if (!this.isPolygonType(feat1.geometry) || !this.isPolygonType(feat2.geometry)) {
            continue;
          }

          const result = intersect(
            featureCollection([
              feat1 as Feature<Polygon | MultiPolygon>,
              feat2 as Feature<Polygon | MultiPolygon>,
            ])
          );

          if (result) {
            // Merge attributes from both features (Requirement 23.3)
            const mergedProperties: GeoJsonProperties = {
              ...this.prefixProperties(feat1.properties, layer1.name),
              ...this.prefixProperties(feat2.properties, layer2.name),
            };
            result.properties = mergedProperties;
            resultFeatures.push(result as Feature);
          }
        }
      }

      if (resultFeatures.length === 0) {
        return {
          success: true,
          features: [],
          message: 'No intersections found between the two layers',
        };
      }

      return {
        success: true,
        features: resultFeatures,
        message: `Found ${resultFeatures.length} intersection(s)`,
      };
    } catch (error) {
      return {
        success: false,
        features: [],
        message: `Intersection operation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Combines features from multiple layers into a single layer.
   * Handles overlapping features by creating separate features for overlap/non-overlap portions.
   *
   * Requirements: 24.1, 24.2, 24.4, 24.5
   */
  union(layers: VectorLayerData[]): SpatialAnalysisResult {
    if (!layers || layers.length === 0) {
      return { success: false, features: [], message: 'No layers provided for union operation' };
    }

    const allFeatures: Feature[] = [];
    for (const layer of layers) {
      allFeatures.push(...layer.features);
    }

    if (allFeatures.length === 0) {
      return { success: true, features: [], message: 'All input layers are empty' };
    }

    try {
      // For polygon features, attempt geometric union
      const polygonFeatures = allFeatures.filter((f) => this.isPolygonType(f.geometry));
      const nonPolygonFeatures = allFeatures.filter((f) => !this.isPolygonType(f.geometry));

      const resultFeatures: Feature[] = [...nonPolygonFeatures];

      if (polygonFeatures.length > 0) {
        // Union polygons pairwise
        let accumulated: Feature<Polygon | MultiPolygon> | null = null;

        for (const feat of polygonFeatures) {
          if (!accumulated) {
            accumulated = feat as Feature<Polygon | MultiPolygon>;
          } else {
            const result = union(
              featureCollection([accumulated, feat as Feature<Polygon | MultiPolygon>])
            );
            if (result) {
              accumulated = result as Feature<Polygon | MultiPolygon>;
            }
          }
        }

        if (accumulated) {
          // Preserve combined attributes
          const combinedProps: GeoJsonProperties = {};
          for (const layer of layers) {
            for (const feat of layer.features) {
              if (this.isPolygonType(feat.geometry) && feat.properties) {
                Object.assign(combinedProps, feat.properties);
              }
            }
          }
          accumulated.properties = combinedProps;
          resultFeatures.push(accumulated as Feature);
        }
      }

      return {
        success: true,
        features: resultFeatures,
        message: `Union created with ${resultFeatures.length} feature(s)`,
      };
    } catch (error) {
      return {
        success: false,
        features: [],
        message: `Union operation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Clips input layer features to a clip boundary.
   * Trims geometries that extend beyond the boundary.
   *
   * Requirements: 25.1, 25.2, 25.4, 25.5
   */
  clip(inputLayer: VectorLayerData, clipLayer: VectorLayerData): SpatialAnalysisResult {
    if (!inputLayer.features.length) {
      return { success: true, features: [], message: 'Input layer is empty' };
    }

    if (!clipLayer.features.length) {
      return { success: false, features: [], message: 'Clip boundary layer is empty' };
    }

    try {
      // Build a combined clip boundary from all clip layer polygons
      const clipPolygons = clipLayer.features.filter((f) => this.isPolygonType(f.geometry));

      if (clipPolygons.length === 0) {
        return {
          success: false,
          features: [],
          message: 'Clip layer must contain polygon features',
        };
      }

      // Union all clip polygons into a single boundary
      let clipBoundary: Feature<Polygon | MultiPolygon> = clipPolygons[0] as Feature<
        Polygon | MultiPolygon
      >;
      for (let i = 1; i < clipPolygons.length; i++) {
        const result = union(
          featureCollection([clipBoundary, clipPolygons[i] as Feature<Polygon | MultiPolygon>])
        );
        if (result) {
          clipBoundary = result as Feature<Polygon | MultiPolygon>;
        }
      }

      const resultFeatures: Feature[] = [];

      for (const feat of inputLayer.features) {
        if (this.isPolygonType(feat.geometry)) {
          const clipped = intersect(
            featureCollection([feat as Feature<Polygon | MultiPolygon>, clipBoundary])
          );
          if (clipped) {
            clipped.properties = { ...feat.properties };
            resultFeatures.push(clipped as Feature);
          }
        } else if (feat.geometry.type === 'Point') {
          if (booleanPointInPolygon(feat as Feature<Point>, clipBoundary)) {
            resultFeatures.push({ ...feat });
          }
        } else if (
          feat.geometry.type === 'LineString' ||
          feat.geometry.type === 'MultiLineString'
        ) {
          const clipped = this.clipLineFeature(
            feat as Feature<LineString | MultiLineString>,
            clipBoundary
          );
          if (clipped) {
            resultFeatures.push(clipped);
          }
        } else if (booleanIntersects(feat, clipBoundary)) {
          resultFeatures.push({ ...feat, properties: { ...feat.properties } });
        }
      }

      return {
        success: true,
        features: resultFeatures,
        message: `Clipped ${resultFeatures.length} feature(s) to boundary`,
      };
    } catch (error) {
      return {
        success: false,
        features: [],
        message: `Clip operation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  private clipLineFeature(
    feature: Feature<LineString | MultiLineString>,
    clipBoundary: Feature<Polygon | MultiPolygon>
  ): Feature<LineString | MultiLineString> | null {
    const boundaryLines = polygonToLine(clipBoundary);
    const sourceLines =
      feature.geometry.type === 'LineString'
        ? [feature.geometry.coordinates]
        : feature.geometry.coordinates;
    const clippedLines = sourceLines.flatMap((coordinates) =>
      this.clipLineCoordinates(coordinates, boundaryLines, clipBoundary)
    );

    if (clippedLines.length === 0) {
      return null;
    }

    const geometry: LineString | MultiLineString =
      clippedLines.length === 1
        ? { type: 'LineString', coordinates: clippedLines[0] }
        : { type: 'MultiLineString', coordinates: clippedLines };

    const { bbox: _staleBbox, ...featureWithoutBbox } = feature;
    return {
      ...featureWithoutBbox,
      geometry,
      properties: { ...feature.properties },
    };
  }

  private clipLineCoordinates(
    coordinates: Position[],
    boundaryLines: ReturnType<typeof polygonToLine>,
    clipBoundary: Feature<Polygon | MultiPolygon>
  ): Position[][] {
    const clippedLines: Position[][] = [];
    let currentLine: Position[] = [];

    const finishCurrentLine = () => {
      if (currentLine.length >= 2) {
        clippedLines.push(currentLine);
      }
      currentLine = [];
    };

    for (let index = 0; index < coordinates.length - 1; index++) {
      const start = coordinates[index];
      const end = coordinates[index + 1];
      const deltaX = end[0] - start[0];
      const deltaY = end[1] - start[1];
      const squaredLength = deltaX * deltaX + deltaY * deltaY;

      if (squaredLength === 0) {
        continue;
      }

      const segment = lineString([start, end]);
      const intersections = lineIntersect(segment, boundaryLines);
      const splitPositions = [
        0,
        ...intersections.features.map(({ geometry }) => {
          const [x, y] = geometry.coordinates;
          return Math.max(
            0,
            Math.min(1, ((x - start[0]) * deltaX + (y - start[1]) * deltaY) / squaredLength)
          );
        }),
        1,
      ]
        .sort((a, b) => a - b)
        .filter(
          (value, position, values) =>
            position === 0 || Math.abs(value - values[position - 1]) > 1e-10
        );

      for (let splitIndex = 0; splitIndex < splitPositions.length - 1; splitIndex++) {
        const startPosition = splitPositions[splitIndex];
        const endPosition = splitPositions[splitIndex + 1];

        if (endPosition - startPosition <= 1e-10) {
          continue;
        }

        const midpoint = this.interpolatePosition(start, end, (startPosition + endPosition) / 2);

        if (!booleanPointInPolygon(point(midpoint), clipBoundary)) {
          finishCurrentLine();
          continue;
        }

        const clippedStart = this.interpolatePosition(start, end, startPosition);
        const clippedEnd = this.interpolatePosition(start, end, endPosition);
        const lastPosition = currentLine[currentLine.length - 1];

        if (!lastPosition || !this.positionsEqual(lastPosition, clippedStart)) {
          finishCurrentLine();
          currentLine.push(clippedStart);
        }
        currentLine.push(clippedEnd);
      }
    }

    finishCurrentLine();
    return clippedLines;
  }

  private interpolatePosition(start: Position, end: Position, fraction: number): Position {
    return start.map((ordinate, index) =>
      index < end.length ? ordinate + (end[index] - ordinate) * fraction : ordinate
    );
  }

  private positionsEqual(first: Position, second: Position): boolean {
    return (
      first.length === second.length &&
      first.every((ordinate, index) => Math.abs(ordinate - second[index]) <= 1e-10)
    );
  }

  /**
   * Queries features based on spatial relationships.
   * Supports: contains, within, intersects, touches, crosses, within-distance.
   *
   * Requirements: 26.1, 26.2, 26.3
   */
  spatialQuery(options: SpatialQueryOptions): SpatialAnalysisResult {
    const {
      referenceFeature,
      targetLayer,
      relationship,
      distance,
      distanceUnits = 'meters',
    } = options;

    if (!referenceFeature) {
      return { success: false, features: [], message: 'No reference feature provided' };
    }

    if (!targetLayer.features.length) {
      return { success: true, features: [], message: 'Target layer is empty' };
    }

    if (relationship === 'within-distance' && (distance === undefined || distance <= 0)) {
      return {
        success: false,
        features: [],
        message: 'Distance must be positive for within-distance query',
      };
    }

    try {
      const matchingFeatures: Feature[] = [];
      const bufferedReference =
        relationship === 'within-distance'
          ? buffer(referenceFeature, distance as number, { units: toTurfUnits(distanceUnits) })
          : null;

      if (relationship === 'within-distance' && !bufferedReference) {
        return {
          success: false,
          features: [],
          message: 'Unable to create a distance boundary for the reference feature',
        };
      }

      for (const targetFeature of targetLayer.features) {
        let matches = false;

        switch (relationship) {
          case 'contains':
            matches = booleanContains(referenceFeature, targetFeature);
            break;
          case 'within':
            matches = booleanWithin(targetFeature, referenceFeature);
            break;
          case 'intersects':
            matches = booleanIntersects(referenceFeature, targetFeature);
            break;
          case 'touches':
            matches = booleanTouches(referenceFeature, targetFeature);
            break;
          case 'crosses':
            matches = booleanCrosses(referenceFeature, targetFeature);
            break;
          case 'within-distance':
            matches = booleanIntersects(targetFeature, bufferedReference!);
            break;
        }

        if (matches) {
          matchingFeatures.push(targetFeature);
        }
      }

      return {
        success: true,
        features: matchingFeatures,
        message:
          matchingFeatures.length > 0
            ? `Found ${matchingFeatures.length} feature(s) matching '${relationship}' relationship`
            : `No features match the '${relationship}' relationship`,
      };
    } catch (error) {
      return {
        success: false,
        features: [],
        message: `Spatial query failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Validates a geometry for topological correctness.
   *
   * Requirements: 22.6, 23.5, 24.5, 25.5
   */
  validateGeometry(geometry: Geometry): ValidationResult {
    if (!geometry || !geometry.type) {
      return { valid: false, errors: ['Geometry is null or missing type'] };
    }

    const errors: string[] = [];

    switch (geometry.type) {
      case 'Point':
        if (!geometry.coordinates || geometry.coordinates.length < 2) {
          errors.push('Point must have at least 2 coordinate values');
        }
        break;

      case 'LineString':
        if (!geometry.coordinates || geometry.coordinates.length < 2) {
          errors.push('LineString must have at least 2 points');
        }
        break;

      case 'Polygon':
        this.validatePolygonGeometry(geometry.coordinates, errors);
        break;

      case 'MultiPolygon':
        for (let i = 0; i < geometry.coordinates.length; i++) {
          this.validatePolygonGeometry(geometry.coordinates[i], errors, `Polygon ${i}`);
        }
        break;

      case 'MultiPoint':
        if (!geometry.coordinates || geometry.coordinates.length === 0) {
          errors.push('MultiPoint must have at least one point');
        }
        break;

      case 'MultiLineString':
        for (let i = 0; i < geometry.coordinates.length; i++) {
          if (geometry.coordinates[i].length < 2) {
            errors.push(`Line ${i}: must have at least 2 points`);
          }
        }
        break;

      default:
        errors.push(`Unsupported geometry type: ${geometry.type}`);
    }

    return { valid: errors.length === 0, errors };
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private validatePolygonGeometry(coordinates: number[][][], errors: string[], prefix = ''): void {
    if (!coordinates || coordinates.length === 0) {
      errors.push(`${prefix ? prefix + ': ' : ''}Polygon must have at least one ring`);
      return;
    }

    for (let i = 0; i < coordinates.length; i++) {
      const ring = coordinates[i];
      const ringLabel = prefix
        ? `${prefix}, Ring ${i}`
        : i === 0
          ? 'Exterior ring'
          : `Interior ring ${i}`;

      if (ring.length < 4) {
        errors.push(`${ringLabel}: must have at least 4 coordinates`);
        continue;
      }

      // Check ring closure
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        errors.push(`${ringLabel}: ring is not closed`);
      }
    }
  }

  private isPolygonType(geometry: Geometry): boolean {
    return geometry.type === 'Polygon' || geometry.type === 'MultiPolygon';
  }

  private prefixProperties(
    properties: GeoJsonProperties | undefined,
    layerName: string
  ): Record<string, unknown> {
    if (!properties) return {};
    const prefixed: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(properties)) {
      // Skip internal properties
      if (key.startsWith('_')) continue;
      prefixed[`${layerName}_${key}`] = value;
    }
    return prefixed;
  }
}

// Export singleton instance
export const spatialAnalysisEngine = new SpatialAnalysisEngine();
export default spatialAnalysisEngine;
